/**
 * Workspace Resolver Service – Phase 3C.
 *
 * Thin wrapper around the Phase 1 workspaceResolver that adds:
 *  - Standardized runtimeContext output shape
 *  - Source + mode tracking metadata (runtimeSource, runtimeMode)
 *  - DEV-mode logging
 *
 * Phase 3C additions:
 *  - RUNTIME_MODE: 'runtime' | 'draft-edit'
 *  - isDraftEditRole(role): determines if a user may edit the workspace draft
 *  - runtimeMode in RuntimeContext
 *
 * NEVER throws. Any failure returns legacyFallback().
 */
import { resolveWorkspace } from '../workspace/workspaceResolver';

export const RUNTIME_SOURCE = Object.freeze({
  LEGACY:    'legacy',
  WORKSPACE: 'workspace',
});

export const RUNTIME_MODE = Object.freeze({
  RUNTIME:    'runtime',
  DRAFT_EDIT: 'draft-edit',
});

const DRAFT_EDIT_ROLES = new Set(['admin', 'superadmin', 'tenant_admin']);

/**
 * Returns true if the given role may enter draft-edit mode on a workspace.
 * @param {string|null} role
 * @returns {boolean}
 */
export const isDraftEditRole = (role) => DRAFT_EDIT_ROLES.has(role);

/**
 * @typedef {Object} RuntimeContext
 * @property {'legacy'|'workspace'}    runtimeSource
 * @property {'runtime'|'draft-edit'}  runtimeMode
 * @property {string|null}             workspaceId
 * @property {string|null}             workspaceVersionId
 * @property {string|null}             workspaceName
 * @property {number}                  schemaVersion
 */

/** @returns {RuntimeContext} */
export const legacyRuntimeContext = () => ({
  runtimeSource:      RUNTIME_SOURCE.LEGACY,
  runtimeMode:        RUNTIME_MODE.RUNTIME,
  workspaceId:        null,
  workspaceVersionId: null,
  workspaceName:      null,
  schemaVersion:      1,
  draftLoaded:        false,
});

/**
 * @param {object}                     workspace    – workspace object from resolution
 * @param {string|null}                versionId    – loaded version id
 * @param {'runtime'|'draft-edit'}     runtimeMode
 * @returns {RuntimeContext}
 */
export const workspaceRuntimeContext = (workspace, versionId = null, runtimeMode = RUNTIME_MODE.RUNTIME, draftLoaded = false) => ({
  runtimeSource:      RUNTIME_SOURCE.WORKSPACE,
  runtimeMode,
  workspaceId:        workspace?.id        ?? null,
  workspaceVersionId: versionId            ?? workspace?.publishedVersionId ?? null,
  workspaceName:      workspace?.name      ?? null,
  schemaVersion:      1,
  draftLoaded,
  draftRevision:      Number.isInteger(workspace?.revision) ? workspace.revision : 0,
});

/**
 * Resolve the runtime source for the current user.
 *
 * @returns {Promise<{ useLegacy: boolean, workspace: object|null, runtimeContext: RuntimeContext }>}
 */
export async function resolveRuntimeSource() {
  const resolution = await resolveWorkspace();

  if (resolution.status === 'unavailable') {
    return {
      useLegacy:      false,
      status:         'unavailable',
      workspace:      null,
      runtimeContext: legacyRuntimeContext(),
    };
  }

  if (resolution.useLegacy || !resolution.workspace) {
    if (import.meta.env?.DEV) {
      console.debug('[WorkspaceRuntimeService] source=legacy', { reason: resolution.source });
    }
    return {
      useLegacy:      true,
      status:         'legacy',
      workspace:      null,
      runtimeContext: legacyRuntimeContext(),
    };
  }

  const workspace = resolution.workspace;

  if (import.meta.env?.DEV) {
    console.debug('[WorkspaceRuntimeService] source=workspace', {
      workspaceId:        workspace.id,
      publishedVersionId: workspace.publishedVersionId,
    });
  }

  return {
    useLegacy:      false,
    status:         'workspace',
    workspace,
    runtimeContext: workspaceRuntimeContext(workspace),
  };
}
